import PropTypes from "prop-types";
import React from "react";
import Skeleton from "react-loading-skeleton";
import classnames from "classnames";

import { authenticatedHeadersHash } from "views_shared/app/javascript/scripts/authentication";
import { enrichDataWithProfiles } from "./DataEnrichment";

const TOTAL_NUMBERED_ITEM_LIMIT = 100;

// Custom hook to handle PR data enrichment
const useEnrichedPrData = (prs) => {
  const [enrichedPrs, setEnrichedPrs] = React.useState([]);

  React.useEffect(() => {
    async function loadDetails() {
      const enriched = await enrichDataWithProfiles({
        apiDataUrl: `${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1/pr_profiles`,
        apiUserProfilesUrl: `${globalThis.Qwoted.SUPABASE_API_EXTERNAL_URL}/rest/v1/user_profiles`,
        data: prs,
        extraHeaders: authenticatedHeadersHash(),
      });
      setEnrichedPrs(enriched);
    }

    setEnrichedPrs(prs);
    loadDetails();
  }, [prs]);

  return enrichedPrs;
};

// Utility function for rendering profile data or skeleton
const profileData = (profile, field) => {
  if (!profile) {
    return field === "profile_image_url" ? <Skeleton width={50} height={50} /> : <Skeleton width="100%" height={20} />;
  }

  const value = profile[field];
  switch (field) {
    case "profile_image_url":
      return (
        <img
          src={value}
          alt="Avatar"
          className="img-fluid rounded-circle"
          style={{ width: "50px", height: "50px", objectFit: "cover" }}
        />
      );
    case "email":
      return <a href={`mailto:${value}`}>{value}</a>;
    default:
      return value;
  }
};

// Renders a table of PR data with toggleable visibility for each row
function TopPrTable({ prs, toggleRowVisibility }) {
  const enrichedPrs = useEnrichedPrData(prs);

  const numberedEnrichedPrs = enrichedPrs.reduce(
    ({ items, index }, current) => {
      // Number non-hidden items up to TOTAL_NUMBERED_ITEM_LIMIT
      if (!current.hidden && index <= TOTAL_NUMBERED_ITEM_LIMIT) {
        return {
          items: [...items, { no: index, ...current }],
          index: index + 1,
        };
      }
      return {
        items: [...items, { no: null, ...current }],
        index,
      };
    },
    { items: [], index: 1 },
  ).items;

  return (
    <table className="table table-striped table-bordered">
      <thead className="thead-dark">
        <tr>
          <th>#</th>
          <th>Avatar</th>
          <th>Full name</th>
          <th>Email</th>
          <th>Title</th>
          <th>Agency</th>
          <th>Pitches</th>
          <th>Direct media reply points</th>
          <th>Pitch speed points</th>
          <th>Conversation outcome points</th>
          <th>Bad behavior points</th>
          <th>Total points</th>
          <th>Profile</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {/* Render a placeholder when there are no entries */}
        {numberedEnrichedPrs.length === 0 && (
          <tr key="no-entries">
            <td colSpan="99">
              <i>No entries</i>
            </td>
          </tr>
        )}
        {/* Render rows for each PR entry */}
        {numberedEnrichedPrs.map((pr) => (
          <tr
            key={pr.profile_id}
            className={classnames({
              "table-secondary text-decoration-line-through opacity-50": pr.hidden,
              "table-success": !!pr.no,
            })}
          >
            {/* Index column */}
            <td>{pr.no}</td>
            {/* Avatar column */}
            <td className="text-center">{profileData(pr.profile, "profile_image_url")}</td>
            {/* Full name column */}
            <td>{profileData(pr.profile, "name")}</td>
            {/* Email */}
            <td>{profileData(pr.profile, "email")}</td>
            {/* Title column */}
            <td>{profileData(pr.profile, "title")}</td>
            {/* Agency column */}
            <td>
              {!pr.profile ? (
                <Skeleton />
              ) : (
                pr.profile.company_slug && (
                  <a href={`/companies/${pr.profile.company_slug}`} target="_blank" rel="noopener noreferrer">
                    {pr.profile.company_name}
                  </a>
                )
              )}
            </td>
            {/* Pitches column */}
            <td>{pr.num_pitches}</td>
            {/* Direct media reply points column */}
            <td>{pr.direct_media_reply_points}</td>
            {/* Pitch speed points column */}
            <td>{pr.pitch_speed_points}</td>
            {/* Conversation outcome points column */}
            <td>{pr.conversation_outcome_points}</td>
            {/* Bad behavior points column */}
            <td>{pr.bad_behavior_points}</td>
            {/* Total points column */}
            <td>{pr.total_points}</td>
            {/* Profile link column */}
            <td>
              {!pr.profile ? (
                <Skeleton width={100} />
              ) : (
                pr.profile.slug && (
                  <a
                    href={`/profiles/${pr.profile.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-primary"
                  >
                    View profile
                  </a>
                )
              )}
            </td>
            {/* Action column */}
            <td>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => toggleRowVisibility(pr.profile_id)}
              >
                {pr.hidden ? "Show" : "Hide"}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

TopPrTable.propTypes = {
  prs: PropTypes.arrayOf(
    PropTypes.shape({
      hidden: PropTypes.bool.isRequired, // Visibility state of the row
      ineligible: PropTypes.bool, // Eligibility state
      profile_id: PropTypes.number.isRequired, // Unique profile identifier
      num_pitches: PropTypes.number.isRequired, // Number of pitches made
      total_points: PropTypes.number.isRequired, // Total points scored
      pitch_speed_points: PropTypes.number, // Points earned for pitch speed
      bad_behavior_points: PropTypes.number, // Points deducted for bad behavior
      direct_media_reply_points: PropTypes.number, // Points earned for direct replies
      conversation_outcome_points: PropTypes.number, // Points earned for conversation outcomes
    }),
  ).isRequired,
  toggleRowVisibility: PropTypes.func.isRequired, // Callback to toggle row visibility
};

export default TopPrTable;
